import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { IContext } from './columns'
import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { ITodolistTemplate } from '~/types/todolist-template'

import columns from './columns'
import SetTodolistTemplateModal from './SetTodolistTemplateModal'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useTodolistTemplatesQuery } from '~/queries/todolist-templates'
import { useUserDetailsQueries } from '~/queries/users'

import './styles.scss'

const TodolistTemplates = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const tableRef = useRef<HTMLDivElement>(null)

  const gridRef = useRef<AgGridReact>(null)

  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false)

  const isAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.TODOLIST.WRITE])

  /** Queries & Mutations */

  const { data: templates, isFetched: isTemplatesFetched } = useTodolistTemplatesQuery()

  const createdByQueries = templates?.map(({ template }) => template.created_by) || []

  const updatedByQueries = templates?.map(({ template }) => template.updated_by) || []

  const userQueries = useUserDetailsQueries([...createdByQueries, ...updatedByQueries])

  const userLoading = userQueries.some((e) => e.isLoading)

  const users: { user_id: string }[] | undefined = !userLoading
    ? userQueries.map(({ data }) => data || { user_id: '' })
    : undefined

  const rowData =
    isTemplatesFetched && users
      ? templates?.map((t) => ({
          ...t,
          template: {
            ...t.template,
            created_by: users?.find((user) => user.user_id === t.template.created_by),
            updated_by: users?.find((user) => user.user_id === t.template.updated_by),
          },
        })) || []
      : undefined

  /** Effects */

  useDocumentTitle('To-do list templates')

  /** Handlers */

  const handleRedirect = (path: string) => navigate(path)

  const handleRowClicked = (evt: RowClickedEvent<ITodolistTemplate>) => {
    if (gridRef.current?.api.getFocusedCell()?.column.getColId() !== 'actions' && evt.data) {
      navigate(`/todolist-templates/${evt.data.template.template_id}`)
    }
  }

  const handleCreateTemplateModalState = (isOpen: boolean) => () => setIsCreateTemplateModalOpen(isOpen)

  const handleCreateSuccess = (templateId: string) => {
    setIsCreateTemplateModalOpen(false)
    navigate(`/todolist-templates/${templateId}`)
  }

  const context: IContext = {
    handleRedirect,
  }

  return (
    <>
      <Layout className="todolists-view" withPadding>
        <ViewTitle className="layout-view-title">App to-do lists templates</ViewTitle>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!isAuthorized}
            addButtonLabel="New template"
            addButtonTooltip={!isAuthorized ? 'unauthorized' : undefined}
            className="todolist-templates-table with-min-height"
            columnDefs={columns}
            context={context}
            entriesName="todolist-templates"
            onAddButtonClick={handleCreateTemplateModalState(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={rowData}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      <SetTodolistTemplateModal
        isOpen={isCreateTemplateModalOpen}
        onClose={handleCreateTemplateModalState(false)}
        onSuccess={handleCreateSuccess}
      />
    </>
  )
}

export default TodolistTemplates
